// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// Include description of object & useful info here, please!
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { clefNames, isClefName } from 'shared/definitions/musicTheory/clefs';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import * as Skills from '../uTheory.Skills/skills';
import KeySigPlayer from './keySigPlayer';
import CustomKeySignature from '../uTheory.Notation/customKeySignature';
import Clef from '../uTheory.Notation/clef';
import KeySignature from '../uTheory.Notation/keySignature';
import Staff from '../uTheory.Notation/staff';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import Music from '../uTheory.Notation/music';
import TimeSignature from '../uTheory.Notation/timeSignature';

type KeySigConstructionOptions = InteractiveOptions;

interface KeySigQuestion {
  keySignature: KeySignature;
  questionText: string;
  clefName: ClefName;
  text: string[];
  skills: string[];
}

export default class KeySigConstruction extends InteractivePrototype {
  keySigPlayer: KeySigPlayer;

  customKeySig: CustomKeySignature;

  checklock: boolean;

  clef: Clef;

  answerAttempts: number;

  declare questions: KeySigQuestion[];

  declare currentQuestion: KeySigQuestion;

  constructor(displayDiv: HTMLDivElement, options: KeySigConstructionOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = KeySigConstruction.registry.skills;
    this.keySigPlayer = new KeySigPlayer();
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      editor: {
        fixedWidth: 150,
        staffSpacing: 90,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        bottom_y_buffer: 15,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        y_buffer: -20,
        ariaTitle: {
          timeSignature: false,
          barline: false,
          rhythm: false,
        },
      },
      notation: false,
      piano: false,
      letters: { checkButton: true, keySigButtons: true },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.init(displayDiv, options);
  }
  // Overriding these methods / properties:

  init(displayDiv: HTMLDivElement, options: KeySigConstructionOptions) {
    options.maxQuestions = options.maxQuestions || 7;
    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  addListeners() {
    this.letterPanel.addKeySigCallback((value: string) => {
      const listeners: { [keyName: string]: Function } = {
        backspace: () => {
          this.customKeySig.delete();
        },
        delete: () => {
          this.customKeySig.delete();
        },
        left: () => {
          this.customKeySig.selectLeft();
        },
        right: () => {
          this.customKeySig.selectRight();
        },
        up: () => {
          this.customKeySig.moveUp();
        },
        down: () => {
          this.customKeySig.moveDown();
        },
      };

      if (listeners.hasOwnProperty(value)) {
        listeners[value]();
      } else if (value === '#' || value === 'b') {
        this.customKeySig.setAccidentalType(value);
      }
    }, this);
    this.letterPanel.addCheckCallback(this.check, this);
  }

  generateQuestions(questionStrings: string[] = this.options.questions) {
    this.questionPrompt = 'Draw the key signature for {question}.';
    if (!this.optionsTrue('testMode')) {
      this.questionPrompt += '<br /><small>#{question} of {question}</small>';
    }
    if (this.options.drill) {
      this.generateDrillQuestions();
    } else {
      this.questions = questionStrings.map(KeySigConstruction.processQuestion);
      questionStrings = questionStrings || this.options.questions;
    }
  }

  generateDrillQuestions() {
    // Get rid of c_major & a_minor; they'll show up anyway:
    const skillsWithoutZeroAccidentals = this.options.skills
      .map(Skills.makeString)
      .filter((s) => s !== 'a_minor' && s !== 'c_major');

    // it may happen that only a_minor or c_major were given as skills... then the question
    // generator would fail, so we add other similar skills.
    if (skillsWithoutZeroAccidentals.length === 0) {
      if (this.options.skills.map(Skills.makeString).find((s) => s === 'a_minor')) {
        skillsWithoutZeroAccidentals.push('sharp_minors');
        skillsWithoutZeroAccidentals.push('flat_minors');
      }
      if (this.options.skills.map(Skills.makeString).find((s) => s === 'c_major')) {
        skillsWithoutZeroAccidentals.push('sharp_majors');
        skillsWithoutZeroAccidentals.push('flat_majors');
      }
    }

    const generator = new QuestionGenerator();
    const numberOfQuestions = this.options.maxQuestions;
    let questions = [];
    const numQuestionsPerSkill =
      Math.ceil(numberOfQuestions / skillsWithoutZeroAccidentals.length) + 1;
    skillsWithoutZeroAccidentals.forEach((skill) => {
      const skillName = Skills.makeString(skill);
      questions = questions.concat(generator[skillName](numQuestionsPerSkill));
    }, this);
    questions = generator.rejectDuplicates(questions, numberOfQuestions);
    questions = questions.map((q) => `${q}${Math.random() > 0.5 ? '/treble' : '/bass'}`);
    this.questions = questions.map(KeySigConstruction.processQuestion);
  }

  static processQuestion(
    questionText: string,
    index: number = 0,
    arr: string[] = []
  ): KeySigQuestion {
    // Question text is format note/keyQuality/clef:
    const questionParts = questionText.split('/');
    const keySignature = new KeySignature(questionParts[0], questionParts[1]);
    const clefName = questionParts[2] || (Math.random() > 0.5 ? 'treble' : 'bass');
    if (!isClefName(clefName)) {
      throw Error(`${clefName} is not a clef name. Must be one of ${clefNames.join(', ')}`);
    }
    const keyName = keySignature.toAccidentalFontString();
    const skillsCalculator = new SkillCalculator();
    return {
      questionText,
      keySignature,
      clefName,
      skills: skillsCalculator.getKeySignatureSkill(keySignature),
      text: [keyName, `${index + 1}`, `${arr.length}`],
    };
  }

  newQuestion() {
    super.newQuestion();
    this.customKeySig.unlockSignature();
    this.checklock = false;
    this.answerAttempts = 0;

    this.clef.setName(this.currentQuestion.clefName);
    this.customKeySig.clear();
    this.editor.redraw();
  }

  initializeMusic() {
    this.clef = new Clef();
    const staff = new Staff(this.clef);
    const measure = new Measure();
    const voice = new Voice(measure);
    voice.setStaff(staff);
    const timeSig = new TimeSignature('4/4');
    timeSig.hidden = true;
    const keySig = new KeySignature('C', 'Major');
    this.music = new Music(staff, voice, keySig, timeSig);
  }

  initializeEditor() {
    super.initializeEditor();
    this.customKeySig = new CustomKeySignature({ editor: this.editor!, accidental: 'b' });
    this.music?.staves[0]!.setCustomKeySignature(this.customKeySig);
  }

  check() {
    if (this.checklock) return;
    this.checklock = true;
    this.answerAttempts++;
    const keyCheck = this.customKeySig.checkKey(this.clef);
    const correct =
      keyCheck instanceof KeySignature &&
      this.currentQuestion.keySignature.equalsOrRelative(keyCheck);

    if (this.optionsTrue('testMode')) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      this.customKeySig.lockSignature();
      this.editor.redraw();
      this.rightAnswer(1200);
    } else {
      let hint: string;
      if (this.answerAttempts > 1) {
        hint = this.customKeySig.hint(this.currentQuestion.keySignature, this.clef, true);
        this.editor.redraw();
        // if (typeof hint !== 'undefined') this.drawHintOnStave(hint);
      }
      this.checklock = false;
      this.playWrongAnswer();
      this.wrongAnswer(hint);
    }
  }

  playAnswer() {
    this.keySigPlayer.playAnswer(this.currentQuestion.keySignature);
  }

  playWrongAnswer() {
    this.keySigPlayer.playWrongAnswer();
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.customKeySig.lockSignature();
    this.editor.redraw();
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  // Used by getAnswerAndData -- don't delete for now
  private drawHintOnStave(hint: string) {
    const y = this.clef.getName() === 'treble' ? '70' : '15';
    const svg = this.editor.renderer.ctx.svg;
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttributeNS(null, 'fill', 'red');
    text.setAttributeNS(null, 'x', '25');
    text.setAttributeNS(null, 'y', y);
    text.setAttributeNS(null, 'font-size', '8px');

    text.textContent = hint;
    svg.appendChild(text);
  }

  getAnswerAndData(correct: boolean) {
    if (correct === false) {
      const hint = this.customKeySig.hint(this.currentQuestion.keySignature, this.clef);
      this.editor.redraw();
      if (typeof hint !== 'undefined') this.drawHintOnStave(hint);
    }

    const answerAndData = {
      question: this.currentQuestion.questionText,
      questionType: 'KeySigConstruction',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: this.customKeySig.toString(),
      studentAnswerSVG: this.editor.exportSVG(),
      correctAnswerText: this.currentQuestion.keySignature.getKeyNotesForClef(this.clef).join(' '),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }

  static registry = {
    description: 'Write Key Signatures',
    skills: ['sharp_majors', 'sharp_minors', 'flat_majors', 'flat_minors', 'a_minor', 'c_major'],
  };
}
